// Deterministic occasion detection + priority resolver. NO AI here (AI never
// guesses dates). Given the authoritative UAE date, it evaluates the registry
// across BOTH calendars and returns the single highest-priority active occasion.
// Hijri dates honour runtime overrides (UAE Official > Admin Emergency > calculated).
import { OCCASIONS } from "./registry";

// --- Hijri conversion (Umm al-Qura-approximate, Kuwaiti algorithm) --------
// Deterministic fallback ONLY. Official UAE / admin overrides take precedence.
function gregorianToHijri(date) {
  const gy = date.getUTCFullYear();
  const gm = date.getUTCMonth() + 1;
  const gd = date.getUTCDate();
  let jd =
    Math.floor((1461 * (gy + 4800 + Math.floor((gm - 14) / 12))) / 4) +
    Math.floor((367 * (gm - 2 - 12 * Math.floor((gm - 14) / 12))) / 12) -
    Math.floor((3 * Math.floor((gy + 4900 + Math.floor((gm - 14) / 12)) / 100)) / 4) +
    gd - 32075;
  const l0 = jd - 1948440 + 10632;
  const n = Math.floor((l0 - 1) / 10631);
  let l = l0 - 10631 * n + 354;
  const j =
    Math.floor((10985 - l) / 5316) * Math.floor((50 * l) / 17719) +
    Math.floor(l / 5670) * Math.floor((43 * l) / 15238);
  l =
    l - Math.floor((30 - j) / 15) * Math.floor((17719 * j) / 50) -
    Math.floor(j / 16) * Math.floor((15238 * j) / 43) + 29;
  const hm = Math.floor((24 * l) / 709);
  const hd = l - Math.floor((709 * hm) / 24);
  const hy = 30 * n + j - 30;
  return { hy, hm, hd };
}

const HMONTHS_EN = ["Muharram", "Safar", "Rabi al-Awwal", "Rabi al-Thani", "Jumada al-Awwal",
  "Jumada al-Thani", "Rajab", "Shaban", "Ramadan", "Shawwal", "Dhu al-Qadah", "Dhu al-Hijjah"];

function parseUaeDate(uaeDateStr) {
  const [y, m, d] = uaeDateStr.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function daysBetween(a, b) {
  return Math.round((a - b) / 86400000);
}

// Start Date (UTC-normalised) for an occasion, honouring runtime Hijri overrides.
function occasionStart(occ, refDate, overrides) {
  if (occ.calendar === "gregorian") {
    return new Date(Date.UTC(refDate.getUTCFullYear(), occ.rule.month - 1, occ.rule.day));
  }
  const ov = overrides && overrides[occ.id];
  if (ov && ov.start) {
    const [y, m, d] = ov.start.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d));
  }
  for (let offset = -1; offset <= 400; offset++) {
    const cand = new Date(refDate.getTime() + offset * 86400000);
    const h = gregorianToHijri(cand);
    if (h.hm === occ.rule.hmonth && h.hd === occ.rule.hday) return cand;
  }
  return null;
}

// overrides: { [occasion_id]: { start: "YYYY-MM-DD", source } } from backend state.
export function detectOccasion(uaeDateStr, overrides = {}) {
  const today = parseUaeDate(uaeDateStr);
  const candidates = [];
  for (const occ of OCCASIONS) {
    if (!occ.active) continue;
    for (const yr of [today.getUTCFullYear(), today.getUTCFullYear() - 1]) {
      const ref = new Date(Date.UTC(yr, today.getUTCMonth(), today.getUTCDate()));
      const start = occ.calendar === "gregorian"
        ? new Date(Date.UTC(yr, occ.rule.month - 1, occ.rule.day))
        : occasionStart(occ, ref, overrides);
      if (!start) continue;
      const diff = daysBetween(today, start);
      if (diff >= 0 && diff < (occ.durationDays || 1)) {
        candidates.push({ occ, dayIndex: diff, start });
        break;
      }
    }
  }
  if (!candidates.length) return null;
  candidates.sort((a, b) => b.occ.priority - a.occ.priority);
  const winner = candidates[0];
  return {
    ...winner.occ,
    dayIndex: winner.dayIndex,
    dateSource: winner.occ.calendar === "hijri"
      ? (overrides[winner.occ.id]?.source || "calculated")
      : "gregorian",
    hijriToday: gregorianToHijri(today),
  };
}

export function occasionById(id) {
  const o = OCCASIONS.find((x) => x.id === id);
  return o ? { ...o, dayIndex: 0 } : null;
}

export function hijriTodayFromUae(uaeDateStr) {
  return gregorianToHijri(parseUaeDate(uaeDateStr));
}

export function formatHijri(uaeDateStr) {
  const h = gregorianToHijri(parseUaeDate(uaeDateStr));
  return { ...h, monthName: HMONTHS_EN[h.hm - 1] || "", text: `${h.hd} ${HMONTHS_EN[h.hm - 1] || ""} ${h.hy} AH` };
}
